import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { API_ENDPOINTS } from '../../../shared/services/api/api-endpoints';
import { ApiService } from '../../../shared/services/api/api.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { environment } from '../../../../environments/environment';
import { LogoHeaderComponent } from '../../../shared/components/logo-header/logo-header.component';

@Component({
  selector: 'business-onboarding',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterModule, LoaderComponent, LogoHeaderComponent],
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss']
})
export class BusinessOnboardingComponent implements OnInit {
  assetUrl = environment.assetBaseUrl

  private router = inject(Router);
  private fb = inject(FormBuilder);
  private ApiService = inject(ApiService)
  

  errorMessage: string = '';

  currentStep = 1;
  totalSteps = 2;

  step1Form!: FormGroup;
  step2Form!: FormGroup;

  step1Data: any = null;
  isLoading: boolean = false;

  ngOnInit(): void {
    this.initializeForms(); // ✅ FIX: forms must be initialized on component load
  }

  // 🔹 Navigation buttons
  navigateToCustomerLogin(): void {
    this.router.navigate(['/login']);
  }

  // 🔹 Navigation buttons
  navigateToBusinessLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToCustomerRegister(): void {
    this.router.navigate(['/sign-up']);
  }

  // 🔹 Init forms
  initializeForms() {
    this.step1Form = this.fb.group({
      full_name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      role_name: ['business_owner']
    }, { validators: this.passwordMatchValidator });

    this.step2Form = this.fb.group({
      business_name: ['', [Validators.required, Validators.minLength(2)]],
      // brand_name: ['', [Validators.required, Validators.minLength(2)]],
      business_type: ['', [Validators.required]]
    });
  }

  // 🔹 Password check
  passwordMatchValidator(group: FormGroup) {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');
    return password && confirmPassword && password.value !== confirmPassword.value
      ? { passwordMismatch: true }
      : null;
  }

  // 🔹 Step 1 submit
  onStep1Submit() {
    if (this.step1Form.valid) {
      this.step1Data = this.step1Form.value;
      this.currentStep = 2; // ✅ go to step 2
    } else {
      this.markFormGroupTouched(this.step1Form);
    }
  }

  // 🔹 Back button
  goBackToStep1() {
    this.currentStep = 1;
  }

  // 🔹 Step 2 submit
  async onStep2Submit() {
    if (this.step2Form.valid && this.step1Data) {
      this.isLoading = true;
      localStorage.removeItem('access_token');
      try {
        const registrationData = {
          full_name: this.step1Data.full_name,
          email: this.step1Data.email,
          password: this.step1Data.password,
          role_name: this.step1Data.role_name,
          business: {
            business_name: this.step2Form.value.business_name,
            brand_name: this.step2Form.value.brand_name,
            business_type: this.step2Form.value.business_type
          }
        };
        this.ApiService
          .request(API_ENDPOINTS.auth.register, 'POST', registrationData, 'Welcome! Onboarding Successful!')
          .subscribe({
            next: (response: any) => {
              this.isLoading = false;
              localStorage.setItem('access_token', response.access);
              this.router.navigateByUrl('/admin/business/overview');
            },
            error: () => (this.isLoading = false),
        });
      } catch (e) {
        console.error('❌ Unexpected error:', e);
        this.isLoading = false;
      }
    } else {
      this.markFormGroupTouched(this.step2Form);
    }
  }

  // 🔹 Helpers
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key)?.markAsTouched();
    });
  }

  getFieldError(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field?.errors && field?.touched) {
      if (field.errors['required']) return `${fieldName.replace('_', ' ')} is required`;
      if (field.errors['email']) return 'Please enter a valid email address';
      if (field.errors['minlength']) return `${fieldName.replace('_', ' ')} must be at least ${field.errors['minlength'].requiredLength} characters`;
    }
    return '';
  }

  passwordVisible: boolean = false
  passwordConfirmVisible: boolean = false
  toggleVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }
  toggleConfirmVisibility(): void {
    this.passwordConfirmVisible = !this.passwordConfirmVisible;
  }

  getFormError(form: FormGroup): string {
    if (form.errors?.['passwordMismatch'] && form.get('confirmPassword')?.touched) {
      return 'Passwords do not match';
    }
    return '';
  }
}